/**
 * parcoursConfigLoader.js
 *
 * Couche d'indirection entre le code et la source de la config parcours.
 *
 * Au démarrage : tente de charger depuis Firestore (parcoursConfig/main).
 * Si version >= 3 → utilise la config Firestore (mode dynamique).
 * Sinon → fallback sur le hardcodé (parcoursStructure.js).
 *
 * Usage :
 *   await loadParcoursConfig();          // Appeler UNE FOIS au démarrage
 *   const chapitres = getChapitres();          // Puis accès synchrone
 *   const msgs = getMarcelMessages();
 */

import ParcoursConfigService from '../services/ParcoursConfigService';
import { PARCOURS_CHAPITRES, MARCEL_MESSAGES, TOTAL_CHAPITRES } from './parcoursStructure';

// ============================================================
// État interne (mémoire)
// ============================================================

let _loadedConfig = null;
let _loadedChapitres = null;
let _loadedMessages = null;
let _isLoaded = false;

// ============================================================
// Chargement
// ============================================================

/**
 * Charge la config du parcours. À appeler au démarrage par ParcoursContext.
 *
 * - Si Firestore a un doc version >= 3 → mode dynamique
 * - Sinon → fallback hardcodé
 *
 * @returns {Promise<Object>} la config chargée
 */
export async function loadParcoursConfig() {
  try {
    const config = await ParcoursConfigService.getConfig();

    // Support clé "phases" (ancien nom Firestore) et "chapitres" (nouveau nom)
    const chapitresData = config?.chapitres || config?.phases || null;
    if (config && config.version >= 3 && chapitresData) {
      // Mode dynamique : Firestore
      _loadedConfig = config;
      _loadedChapitres = chapitresData;
      _loadedMessages = Array.isArray(config.marcelMessages) ? config.marcelMessages : [];
      _isLoaded = true;
      return config;
    }
  } catch (e) {
    console.warn('[ParcoursConfigLoader] Firestore load failed, using hardcoded:', e.message);
  }

  // Fallback hardcodé
  _loadedChapitres = PARCOURS_CHAPITRES;
  _loadedMessages = MARCEL_MESSAGES;
  _loadedConfig = { version: 2, chapitres: PARCOURS_CHAPITRES, marcelMessages: MARCEL_MESSAGES };
  _isLoaded = true;
  return _loadedConfig;
}

// ============================================================
// Accesseurs synchrones (après loadParcoursConfig)
// ============================================================

/**
 * Tous les chapitres.
 * @returns {Object} { 1: { name, type, steps }, 2: { ... }, ... }
 */
export function getChapitres() {
  return _loadedChapitres || PARCOURS_CHAPITRES;
}

/**
 * Config d'un chapitre spécifique.
 * @param {number} chapitreNumber
 * @returns {Object|null}
 */
export function getChapitreConfig(chapitreNumber) {
  const chapitres = getChapitres();
  return chapitres[chapitreNumber] || null;
}

/**
 * Steps d'un chapitre (linéaire).
 * @param {number} chapitreNumber
 * @returns {Array}
 */
export function getChapitreSteps(chapitreNumber) {
  const chapitre = getChapitreConfig(chapitreNumber);
  if (!chapitre) return [];
  return chapitre.steps || [];
}

/**
 * Messages Marcel.
 * @returns {Array}
 */
export function getMarcelMessages() {
  return _loadedMessages || MARCEL_MESSAGES;
}

/**
 * Nombre total de chapitres.
 * @returns {number}
 */
export function getTotalChapitres() {
  const chapitres = getChapitres();
  return Object.keys(chapitres).length || TOTAL_CHAPITRES;
}

/**
 * Vérifier si la config est chargée depuis Firestore (v3+).
 * @returns {boolean}
 */
export function isFirestoreConfig() {
  return _loadedConfig?.version >= 3;
}

/**
 * Vérifier si la config a été chargée.
 * @returns {boolean}
 */
export function isConfigLoaded() {
  return _isLoaded;
}

// ============================================================
// Invalidation (après save admin)
// ============================================================

/**
 * Vider le cache et forcer un rechargement au prochain loadParcoursConfig().
 */
export function invalidateConfig() {
  _loadedConfig = null;
  _loadedChapitres = null;
  _loadedMessages = null;
  _isLoaded = false;
  ParcoursConfigService.invalidateCache();
}

/**
 * Force un rechargement depuis Firestore SANS vider _loadedChapitres pendant l'opération.
 * Utiliser à la place de invalidateConfig() + loadParcoursConfig() pour éviter la race
 * condition où refreshProgression() lirait _loadedChapitres = null et calculerait
 * isChapitreComplete = false (fallback vide).
 *
 * _loadedChapitres reste lisible avec l'ancienne valeur jusqu'à ce que les nouvelles
 * données Firestore soient prêtes, puis est mis à jour atomiquement.
 */
export async function forceRefreshConfig() {
  ParcoursConfigService.invalidateCache();
  try {
    const config = await ParcoursConfigService.getConfig();
    const chapitresData = config?.chapitres || config?.phases || null;
    if (config && config.version >= 3 && chapitresData) {
      _loadedConfig = config;
      _loadedChapitres = chapitresData;
      _loadedMessages = Array.isArray(config.marcelMessages) ? config.marcelMessages : [];
      _isLoaded = true;
    }
  } catch (e) {
    console.warn('[ParcoursConfigLoader] forceRefreshConfig failed:', e.message);
  }
}

// ============================================================
// Migration : convertir le hardcodé en schéma v3
// ============================================================

/**
 * Convertit la structure hardcodée (PARCOURS_CHAPITRES + MARCEL_MESSAGES)
 * vers le schéma Firestore v3.
 *
 * Utilisé par l'admin pour initialiser Firestore avec la config existante.
 *
 * @returns {Object} config v3 prête à sauvegarder
 */
export function convertHardcodedToV3Schema() {
  const v3 = {
    version: 3,
    chapitres: {},
    marcelMessages: []
  };

  // Convertir chaque chapitre
  for (const [chapitreId, chapitre] of Object.entries(PARCOURS_CHAPITRES)) {
    if (chapitre.type === 'linear') {
      v3.chapitres[chapitreId] = {
        name: chapitre.name,
        type: 'linear',
        steps: chapitre.steps.map(step => ({
          id: step.id,
          type: step.type,
          label: step.label,
          description: step.description || '',
          icon: step.icon || step.type,
          params: extractStepParams(step)
        }))
      };
    } else if (chapitre.type === 'alternating') {
      // Convertir le chapitre alternante en un chapitre linéaire vide
      // (l'admin pourra la remplir manuellement)
      v3.chapitres[chapitreId] = {
        name: chapitre.name,
        type: 'linear',
        steps: []
      };
    }
  }

  // Convertir les messages Marcel
  v3.marcelMessages = MARCEL_MESSAGES.map(msg => ({
    afterStepId: msg.afterStepId,
    conditionStepId: msg.conditionStepId,
    image: msg.image,
    message: msg.defaultMessage
  }));

  return v3;
}

/**
 * Extrait les params type-spécifiques d'un step hardcodé.
 * @param {Object} step - step au format parcoursStructure.js
 * @returns {Object} params au format v3
 */
function extractStepParams(step) {
  const params = {};

  switch (step.type) {
    case 'discovery':
      params.exerciseId = step.exerciseId;
      params.path = step.path;
      break;

    case 'rhythm-group':
      params.orderRange = step.orderRange;
      break;

    case 'quiz':
      params.tier = step.tier;
      break;

    case 'drill':
      params.drillName = step.drillName;
      break;

    case 'memorization':
      params.tier = step.tier;
      break;

    case 'combo':
      params.activities = (step.activities || []).map(a => ({
        id: a.id,
        type: a.type,
        label: a.label,
        params: extractStepParams(a)
      }));
      break;

    default:
      break;
  }

  return params;
}
